import {useContext, useMemo, useState} from "react";
import {Navigate, useNavigate, useParams} from "react-router";
import {useTranslation} from "react-i18next";
import {faChevronDown, faMagnifyingGlass, faSliders, faWaveform, faXmark} from "@fortawesome/pro-solid-svg-icons";

import {Styled} from "./RentPageList.styles"
import ProductCard from "./ProductCard";
import ActionButton from "../../lib/UIBookBrd/ActionButton";
import {Local_Data} from "../../Context";
import {RENT_PAGE} from "../../Routes";
import {
    CATEGORY_BY_SLUG,
    filterByCategory,
    getProductName,
    getProductPath,
    hasNumericPrice,
    PRODUCT_CATEGORIES,
    ProductEntry,
    toProductList
} from "../../lib/products";

type SortKey = 'recommended' | 'price_asc' | 'price_desc' | 'name'

const TABS = [
    {slug: 'Wybor', labelKey: 'lbl_all', category: undefined},
    {slug: 'Sprzet-DJ', labelKey: 'lbl_dj_equipment', category: PRODUCT_CATEGORIES.DJ},
    {slug: 'Sprzet-Sceniczny', labelKey: 'lbl_small_event_equipment', category: PRODUCT_CATEGORIES.SCENE}
]

const SORT_OPTIONS: SortKey[] = ['recommended', 'price_asc', 'price_desc', 'name']

const SKELETON_COUNT = 8

const sortProducts = (list: ProductEntry[], sort: SortKey, t: (key: string) => string) => {
    const priceOf = ({value}: ProductEntry) => (hasNumericPrice(value?.rentPrice) ? value.rentPrice : Number.MAX_SAFE_INTEGER)
    switch (sort) {
        case 'price_asc':
            return [...list].sort((first, second) => priceOf(first) - priceOf(second))
        case 'price_desc':
            return [...list].sort((first, second) => {
                const secondPrice = hasNumericPrice(second.value?.rentPrice) ? second.value.rentPrice : -1
                const firstPrice = hasNumericPrice(first.value?.rentPrice) ? first.value.rentPrice : -1
                return secondPrice - firstPrice
            })
        case 'name':
            return [...list].sort((first, second) => (
                getProductName(first.id, first.value, t).localeCompare(getProductName(second.id, second.value, t))
            ))
        default:
            // Sets and hot-priced gear first, then the most expensive kit.
            return [...list].sort((first, second) => {
                const flagged = Number(!!second.value?.isSpecialPrice) - Number(!!first.value?.isSpecialPrice)
                if (flagged !== 0) {
                    return flagged
                }
                const firstPrice = hasNumericPrice(first.value?.rentPrice) ? first.value.rentPrice : -1
                const secondPrice = hasNumericPrice(second.value?.rentPrice) ? second.value.rentPrice : -1
                return secondPrice - firstPrice
            })
    }
}

const RentPageList = () => {

    const {choice} = useParams()

    const navigate = useNavigate()

    const {t} = useTranslation()

    const [data] = useContext<any>(Local_Data)

    const [query, setQuery] = useState<string>('')

    const [sort, setSort] = useState<SortKey>('recommended')

    const {Products, isLoadingProducts} = data ?? {}

    const allProducts = useMemo(() => toProductList(Products), [Products])

    const activeTab = TABS.find((tab) => tab.slug === choice) ?? TABS[0]

    const results = useMemo(() => {
        const byCategory = filterByCategory(allProducts, activeTab.category)
        const needle = query.trim().toLowerCase()
        const searched = !needle ? byCategory : byCategory.filter(({id, value}) => (
            [id, getProductName(id, value, t), value?.producent, value?.type]
                .filter(Boolean)
                .some((field) => (field as string).toLowerCase().includes(needle))
        ))
        return sortProducts(searched, sort, t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allProducts, activeTab.category, query, sort])

    // Legacy links pointed straight at `/Wynajem/<product>`; keep them alive.
    if (choice && !CATEGORY_BY_SLUG.hasOwnProperty(choice) && Products?.[choice]) {
        return (<Navigate to={getProductPath(choice)} replace/>)
    }

    const isLoading = !!isLoadingProducts && !allProducts.length

    return (
        <Styled.Page>
            <Styled.Head>
                <Styled.HeadText>
                    <Styled.Eyebrow>{t('lbl_rent_equipment')}</Styled.Eyebrow>
                    <Styled.Title>{t('lbl_offer_title')}</Styled.Title>
                    <Styled.Lead>{t('lbl_offer_lead')}</Styled.Lead>
                </Styled.HeadText>
                <Styled.HeadCount>
                    {`${results.length} ${t('lbl_positions')}`}
                </Styled.HeadCount>
            </Styled.Head>

            <Styled.Toolbar>
                <Styled.ToolbarInner>
                    <Styled.Tabs>
                        {TABS.map((tab) => {
                            const isActive = tab.slug === activeTab.slug
                            return (
                                <Styled.Tab
                                    key={tab.slug}
                                    $active={isActive}
                                    onClick={() => navigate(`${RENT_PAGE}/${tab.slug}`)}
                                >
                                    {t(tab.labelKey)}
                                    <Styled.TabCount $active={isActive}>
                                        {filterByCategory(allProducts, tab.category).length}
                                    </Styled.TabCount>
                                </Styled.Tab>
                            )
                        })}
                    </Styled.Tabs>

                    <Styled.ToolbarRight>
                        <Styled.Search>
                            <Styled.SearchIcon icon={faMagnifyingGlass}/>
                            <Styled.SearchInput
                                value={query}
                                placeholder={t('lbl_search_gear')}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
                            />
                            {!!query && (
                                <Styled.ClearSearch
                                    onClick={() => setQuery('')}
                                    aria-label={t('lbl_clear')}
                                >
                                    <Styled.SearchIcon icon={faXmark}/>
                                </Styled.ClearSearch>
                            )}
                        </Styled.Search>

                        <Styled.SortHolder>
                            <Styled.SortIcon icon={faSliders}/>
                            <Styled.Sort
                                value={sort}
                                aria-label={t('lbl_sort')}
                                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setSort(event.target.value as SortKey)}
                            >
                                {SORT_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {t(`lbl_sort_${option}`)}
                                    </option>
                                ))}
                            </Styled.Sort>
                            <Styled.SortChevron icon={faChevronDown}/>
                        </Styled.SortHolder>
                    </Styled.ToolbarRight>
                </Styled.ToolbarInner>
            </Styled.Toolbar>

            {isLoading ? (
                <Styled.Grid>
                    {Array.from({length: SKELETON_COUNT}).map((_, index) => (
                        <Styled.SkeletonCard key={index}/>
                    ))}
                </Styled.Grid>
            ) : results.length ? (
                <Styled.Grid>
                    {results.map(({id, value}, index) => (
                        <ProductCard
                            key={id}
                            id={id}
                            value={value}
                            index={index}
                        />
                    ))}
                </Styled.Grid>
            ) : (
                <Styled.Empty>
                    <Styled.EmptyIcon icon={faWaveform}/>
                    <Styled.EmptyTitle>{t('lbl_no_results')}</Styled.EmptyTitle>
                    <Styled.EmptyText>{t('lbl_no_results_desc')}</Styled.EmptyText>
                    <ActionButton
                        variant={'outline'}
                        label={t('lbl_clear_filters')}
                        onClick={() => {
                            setQuery('')
                            navigate(`${RENT_PAGE}/Wybor`)
                        }}
                    />
                </Styled.Empty>
            )}
        </Styled.Page>
    )
}

export default RentPageList
